
// Default SEO configuration
const defaultSEO = {
  siteName: "CMS | PECB Training Partner",
  siteUrl: "https://comply-cms.com",
  defaultTitle: "PECB Certified Training Courses | ISO Certification | Compliance Management Systems",
  defaultDescription: "Advance your career with PECB certified training courses. ISO 9001, ISO 27001, ISO 45001, ISO 14001, ISO 22000, AI Management (ISO 42001) and more. Serving professionals in the United States, Jamaica, the Caribbean & internationally.",
  defaultImage: "https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg",
  twitterHandle: "@complycms",
  locale: "en_US",
};

// SEO component for consistent meta tags across pages
export const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = "website",
  keywords = [],
  noIndex = false,
  structuredData = null,
  article = null
}) => {
  // Smart title handling - don't append siteName if title already contains key brand terms
  const brandTerms = ["Compliance Management Systems", "CMS", "Comply-CMS"];
  const titleHasBrand = title && brandTerms.some(term => title.toLowerCase().includes(term.toLowerCase()));
  
  const seoTitle = title 
    ? (titleHasBrand ? title : `${title} | ${defaultSEO.siteName}`)
    : defaultSEO.defaultTitle;
  
  const seoDescription = description || defaultSEO.defaultDescription;
  const seoImage = image || defaultSEO.defaultImage;
  const seoUrl = url ? `${defaultSEO.siteUrl}${url}` : defaultSEO.siteUrl;
  
  const defaultKeywords = [
    "PECB certification",
    "ISO training",
    "Lead Auditor training",
    "Lead Implementer course",
    "ISO 9001 certification",
    "ISO 27001 training",
    "ISO 45001 course",
    "ISO 14001 certification",
    "Jamaica ISO training",
    "ISO training USA",
    "PECB training United States",
    "ISO 9001 Lead Auditor training USA",
    "ISO consultant Mississippi",
    "Caribbean PECB courses",
    "professional certification",
    "management system training",
    "quality management",
    "information security",
    "environmental management",
    "occupational health safety"
  ];
  
  const allKeywords = [...new Set([...keywords, ...defaultKeywords])].join(", ");

  return (
    <>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={seoUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={defaultSEO.locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      
      {/* Article specific (for blog posts) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
        </>
      )}
      
      {/* Geo targeting: US (Madison, MS) + Jamaica/Caribbean */}
      <meta name="geo.region" content="US-MS" />
      <meta name="geo.placename" content="Madison, Mississippi" />
      <meta name="geo.position" content="32.4618;-90.1154" />
      <meta name="ICBM" content="32.4618, -90.1154" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </>
  );
};

// Organization structured data
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": "Compliance Management Systems",
  "alternateName": "Comply CMS",
  "url": "https://comply-cms.com",
  "logo": `${defaultSEO.siteUrl}/logo.png`,
  "description": "Authorized PECB Training Partner offering ISO certification courses in the United States, Jamaica, the Caribbean, and internationally.",
  "address": [
    { "@type": "PostalAddress", "addressLocality": "Madison", "addressRegion": "MS", "addressCountry": "US" },
    { "@type": "PostalAddress", "addressLocality": "Kingston", "addressCountry": "JM" }
  ],
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+1-876-833-3918",
      "contactType": "customer service",
      "areaServed": ["JM", "BB", "TT", "GY", "US", "CA", "GB"],
      "availableLanguage": "English"
    },
    {
      "@type": "ContactPoint",
      "telephone": "+1-954-830-1186",
      "contactType": "customer service",
      "areaServed": "US",
      "availableLanguage": "English"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/cmsolutionsja/",
    "https://www.facebook.com/cmsolutionsja/",
    "https://www.instagram.com/cmsolutions_ja"
  ],
  "hasCredential": {
    "@type": "EducationalOccupationalCredential",
    "credentialCategory": "Authorized Training Partner",
    "recognizedBy": {
      "@type": "Organization",
      "name": "PECB",
      "url": "https://pecb.com"
    }
  }
});

// Course structured data generator
export const getCourseSchema = (course) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": course.title,
  "description": course.description,
  "provider": {
    "@type": "EducationalOrganization",
    "name": "Compliance Management Systems",
    "url": defaultSEO.siteUrl
  },
  "educationalCredentialAwarded": `PECB Certified ${course.certification_level}`,
  "courseCode": course.id,
  "hasCourseInstance": course.delivery_formats?.map(format => ({
    "@type": "CourseInstance",
    "courseMode": format.format === "online-live" ? "online" : format.format === "e-learning" ? "online" : "mixed",
    "duration": format.duration,
    "offers": {
      "@type": "Offer",
      "price": format.price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString()
    }
  })),
  "about": {
    "@type": "Thing",
    "name": course.category
  },
  "teaches": course.learning_outcomes?.join(", "),
  "coursePrerequisites": course.prerequisites || "None",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "150",
    "bestRating": "5",
    "worstRating": "1"
  }
});

// Course listing page structured data
export const getCourseListSchema = (courses) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "PECB Certified Training Courses",
  "description": "Browse our selection of PECB certified training courses for ISO management systems",
  "numberOfItems": courses.length,
  "itemListElement": courses.slice(0, 10).map((course, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Course",
      "name": course.title,
      "description": course.description?.substring(0, 150) + "...",
      "url": `${defaultSEO.siteUrl}/courses/${course.id}`,
      "provider": {
        "@type": "EducationalOrganization",
        "name": "Compliance Management Systems"
      }
    }
  }))
});

// FAQ Page structured data
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Breadcrumb structured data
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${defaultSEO.siteUrl}${item.url}`
  }))
});

// Local Business structured data for Jamaica
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Compliance Management Systems",
  "image": defaultSEO.defaultImage,
  "url": "https://comply-cms.com",
  "telephone": "+1-954-830-1186",
  "email": "info@comply-cms.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Madison",
    "addressRegion": "MS",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 32.4618,
    "longitude": -90.1154
  },
  "department": {
    "@type": "ProfessionalService",
    "name": "Compliance Management Systems Jamaica",
    "telephone": "+1-876-833-3918",
    "address": { "@type": "PostalAddress", "addressLocality": "Kingston", "addressCountry": "JM" }
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "09:00",
      "closes": "13:00"
    }
  ],
  "priceRange": "$375 - $1250",
  "areaServed": [
    { "@type": "Country", "name": "United States" },
    { "@type": "State", "name": "Mississippi" },
    { "@type": "Country", "name": "Jamaica" },
    { "@type": "Country", "name": "Barbados" },
    { "@type": "Country", "name": "Trinidad and Tobago" },
    { "@type": "Country", "name": "Guyana" },
    { "@type": "Country", "name": "Canada" },
    { "@type": "Country", "name": "United Kingdom" }
  ]
});

export default SEO;
